import * as THREE from 'three';
import { CAMERA_FRAMING_MINIMUM_DEPTH } from '../../_data/cameraFraming.js';
export function validateCameraFraming(framing) {
    for (const vector of [framing.bounds.center, framing.direction]) {
        if (vector.length !== 3 || !vector.every(Number.isFinite)) {
            throw new Error('Camera framing requires three finite coordinates for center and direction.');
        }
    }
    if (framing.framingKind === 'extent') {
        if (framing.bounds.points.length === 0 || framing.bounds.points.some((point) => point.length !== 3 || !point.every(Number.isFinite))) {
            throw new Error('Camera extent framing requires nonempty finite three-coordinate points.');
        }
        if (framing.minimumDepth !== undefined && (!Number.isFinite(framing.minimumDepth) || framing.minimumDepth <= 0)) {
            throw new Error('Camera extent framing minimum depth must be finite and positive.');
        }
    }
    else if (framing.framingKind !== undefined && framing.framingKind !== 'sphere') {
        throw new Error('Unsupported camera framing kind.');
    }
    else if (!Number.isFinite(framing.bounds.radius) || framing.bounds.radius <= 0) {
        throw new Error('Camera framing radius must be finite and positive.');
    }
    const directionLength = Math.hypot(...framing.direction);
    if (!Number.isFinite(directionLength) || directionLength === 0) {
        throw new Error('Camera framing direction must have a finite nonzero length.');
    }
    if (!Number.isFinite(framing.padding) || framing.padding < 1) {
        throw new Error('Camera framing padding must be finite and at least one.');
    }
    if (framing.fov !== undefined && (!Number.isFinite(framing.fov) || framing.fov <= 0 || framing.fov >= 180)) {
        throw new Error('Camera framing FOV must be between zero and 180 degrees.');
    }
}
export function resolveCameraFramingPose(framing, start, aspect) {
    validateCameraFraming(framing);
    if (!Number.isFinite(aspect) || aspect <= 0)
        throw new Error('Camera framing requires a positive viewport aspect ratio.');
    const fov = framing.fov ?? start.fov;
    if (!Number.isFinite(fov) || fov <= 0 || fov >= 180)
        throw new Error('Camera framing FOV must be between zero and 180 degrees.');
    const verticalHalfFov = fov * Math.PI / 360;
    const horizontalHalfFov = Math.atan(Math.tan(verticalHalfFov) * aspect);
    const distance = framing.framingKind === 'extent'
        ? resolveExtentDistance(framing, verticalHalfFov, horizontalHalfFov)
        : framing.bounds.radius * framing.padding / Math.sin(Math.min(verticalHalfFov, horizontalHalfFov));
    if (!Number.isFinite(distance))
        throw new Error('Camera framing produced a non-finite distance.');
    const directionLength = Math.hypot(...framing.direction);
    const [x, y, z] = framing.bounds.center;
    const pose = {
        position: [
            x + framing.direction[0] / directionLength * distance,
            y + framing.direction[1] / directionLength * distance,
            z + framing.direction[2] / directionLength * distance,
        ],
        target: [x, y, z],
        fov,
    };
    if (!pose.position.every(Number.isFinite))
        throw new Error('Camera framing produced a non-finite position.');
    return pose;
}
function resolveExtentDistance(framing, verticalHalfFov, horizontalHalfFov) {
    const center = new THREE.Vector3(...framing.bounds.center);
    const backward = new THREE.Vector3(...framing.direction).divideScalar(Math.hypot(...framing.direction));
    // Matrix4.lookAt uses the same fixed-up and parallel-direction handling as the desktop camera.
    const basis = new THREE.Matrix4().lookAt(backward, new THREE.Vector3(), new THREE.Vector3(0, 1, 0));
    const right = new THREE.Vector3().setFromMatrixColumn(basis, 0);
    const up = new THREE.Vector3().setFromMatrixColumn(basis, 1);
    const depthAxis = new THREE.Vector3().setFromMatrixColumn(basis, 2);
    const cameraDepth = backward.dot(depthAxis);
    const horizontalSlope = Math.tan(horizontalHalfFov);
    const verticalSlope = Math.tan(verticalHalfFov);
    const horizontalDenominator = cameraDepth - Math.abs(backward.dot(right)) / horizontalSlope;
    const verticalDenominator = cameraDepth - Math.abs(backward.dot(up)) / verticalSlope;
    if (horizontalDenominator <= 0 || verticalDenominator <= 0) {
        throw new Error('Camera framing direction and FOV cannot produce a fixed-up perspective fit.');
    }
    const minimumDepth = framing.minimumDepth ?? CAMERA_FRAMING_MINIMUM_DEPTH;
    let distance = minimumDepth;
    for (const point of framing.bounds.points) {
        const relative = new THREE.Vector3(...point).sub(center).multiplyScalar(framing.padding);
        const depth = relative.dot(depthAxis);
        distance = Math.max(distance, (depth + Math.abs(relative.dot(right)) / horizontalSlope) / horizontalDenominator, (depth + Math.abs(relative.dot(up)) / verticalSlope) / verticalDenominator, (depth + minimumDepth) / cameraDepth);
    }
    return distance;
}
